import {
    seeProjectPrice,
    userCanSeeBestPrice,
    userCanSeeBestPriceOnProductCard
} from '@ab-core-functions/user-permission';
import type { MappedProduct } from '@ab-core/graphql/dist';
import { useAuth } from '@ab-core/hooks';
import { useBestPrices, useProductsAvailabilitiesWithChannel } from '@ab-core/hooks-v2';
import getActiveCart from '@ab-core/hooks/cartNew/activeCart/useActiveCart';
import type { DefaultScalableProductCardProps } from '../cardLayout';
import { fallBackPosition, fallBackQuantityUnit, fallBackSalesUnit } from './fallBackStates';
import { isItShopProduct } from './utils';

const hasBonus = (bonuses: MappedProduct['bonus'], bonusType: string): boolean => {
    return bonuses?.includes(bonusType) || false;
};

const mapProductTags = (product: MappedProduct, isItShop: boolean) => ({
    product: {
        isAbakus: hasBonus(product.bonus, 'abakusBonus'),
        isAbakusPlus: hasBonus(product.bonus, 'abakusPlusBonus'),
        topProduct: hasBonus(product.bonus, 'topProduct'),
        isTecselect: hasBonus(product.bonus, 'tecselectBonus'),
        is125Premium: hasBonus(product.bonus, 'premium'),
        is125Vip: hasBonus(product.bonus, 'vip'),
        isAdditionalSalesPromotion: hasBonus(product.bonus, 'additionalSalesPromotion'),
        isPromotion: product.isPromotion,
        scoreCardIndex: product.scoreCardIndex,
        isFiveYearGuarantee: product.fiveYearGuarantee,
        purchase: product.purchase,
        successorProduct: product.successorProduct,
        ean: product.ean,
        isItShop
    }
});

const checkIsUnqualifiedContractProduct = (
    cocontractors: Array<string> | undefined,
    productCocontractor: boolean,
    productCocontractorValue?: string
): boolean => {
    if (!productCocontractor) {
        return false;
    }

    if (cocontractors && productCocontractorValue) {
        return !cocontractors.includes(productCocontractorValue);
    }

    return true;
};

export const useMappedProductToScalableProduct = (
    products?: Array<MappedProduct>
): Array<DefaultScalableProductCardProps['product']> => {
    const { activeCart } = getActiveCart();
    const { user } = useAuth();
    const hasBestPrice = userCanSeeBestPrice();
    const hasBestPriceOnProductCard = userCanSeeBestPriceOnProductCard();
    const canSeeProjectPrice = seeProjectPrice();

    let projectNumber =
        activeCart?.custom.customFieldsRaw.find((element) => element.name === 'cart-projectNr')?.value || '0';

    projectNumber = canSeeProjectPrice ? projectNumber : '0';

    const skus: Array<string> = [];
    products?.map((product) => skus.push(product?.sku));

    const { bestPrices } = useBestPrices(skus, !hasBestPrice || !hasBestPriceOnProductCard);
    const { productsAvailabilitiesWithChannel } = useProductsAvailabilitiesWithChannel(
        {
            skus,
            cartId: activeCart?.id
        },
        !activeCart
    );

    const productList: Array<DefaultScalableProductCardProps['product']> = [];

    products?.forEach((product) => {
        const productAvailability = productsAvailabilitiesWithChannel?.find(
            (available) => available.sku === product.sku
        );
        const aggregatedQuantity = productAvailability?.aggregatedMainChannelQuantity;
        const isItShop = isItShopProduct(product.supplierId, aggregatedQuantity);
        const maxQuantity = product.isLimitedQuantity ? aggregatedQuantity : undefined;
        const position = product.position || fallBackPosition;

        productList.push({
            defaultQuantity: product.salesUnit || fallBackSalesUnit,
            sku: product.sku,
            information: { reach: false },
            tags: mapProductTags(product, isItShop),
            ids: {
                articleNumber: product.sku,
                ean: product.ean,
                customerArticleNumber: product.customerArticleNumber
            },
            image: {
                imgSrc: product.image || '',
                sku: product.sku,
                position
            },
            name: { text: product.productName, sku: product.sku, position },
            additionalLinks: {
                sku: product.sku,
                hasAccessories: product.hasAccessories,
                hasAlternatives: product.hasAlternatives
            },
            prices: {
                quantityUnit: product.quantityUnit || fallBackQuantityUnit,
                salesUnit: product.salesUnit || fallBackSalesUnit,
                hidePrice: isItShop
            },
            availability: {
                availability: productAvailability,
                quantityUnit: product.quantityUnit || fallBackQuantityUnit
            },
            order: {
                cableSnaps: product?.cableSnaps,
                bestPrice: bestPrices?.find((bestPrice) => bestPrice.sku === product.sku),
                packagingSize: product?.packagingSize || '',
                quantityUnit: product.quantityUnit || fallBackQuantityUnit,
                salesUnit: product.salesUnit || fallBackSalesUnit,
                sku: product.sku,
                isUnqualifiedContractProduct: checkIsUnqualifiedContractProduct(
                    user?.cocontractors,
                    product.productCocontractor || false,
                    product.productCocontractorValue
                ),
                cartId: activeCart?.id,
                supplierId: product.supplierId,
                stockQuantity: aggregatedQuantity ?? 0,
                successorProduct: product.successorProduct,
                ean: product.ean,
                hidePrice: isItShop,
                position,
                maxQuantity,
                projectNumber
            },
            comparison: {
                sku: product.sku,
                title: product.productName,
                imageSrc: product.image || ''
            }
        });
    });

    return productList;
};
